import { supabase } from "@/integrations/supabase/client";
import imageCompression from "browser-image-compression";

/* -------------------------------------------------------------------------- */
/*                             Public Constants                               */
/* -------------------------------------------------------------------------- */

export const SUPPORTED_IMAGE_FORMATS = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
];

export const SUPPORTED_VIDEO_FORMATS = [
  "video/mp4",
  "video/mpeg",
  "video/3gpp",
  "video/avi",
  "video/quicktime",
];

export const MAX_IMAGE_SIZE = 50 * 1024 * 1024; // 50 MB
export const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500 MB

/* -------------------------------------------------------------------------- */
/*                             Internal Helpers                               */
/* -------------------------------------------------------------------------- */

const UPLOAD_TIMEOUT_MS = 120_000;
const COMPRESSION_TIMEOUT_MS = 30_000;

/** Wraps a promise with a race against a timeout. */
const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> => {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s – please retry.`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId);
  }) as Promise<T>;
};

/** 
 * Sanitise the storage object key to prevent hangs/edge-case issues
 * – removes Arabic chars, special characters, keeps ASCII-safe.
 */
const sanitizeStorageKey = (key: string): string =>
  key
    // Remove Arabic/Persian ranges
    .replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, "")
    // Keep only ASCII alphanumeric, dot, dash, underscore
    .replace(/[^A-Za-z0-9_.-]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 200);

/**
 * Compress an image using browser-image-compression.
 * Returns original file on failure so the upload can proceed with the original.
 */
const compressImage = async (file: File): Promise<File> => {
  try {
    return await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      preserveExif: true,
    });
  } catch (error) {
    console.warn("[fileUpload] Image compression failed, using original file:", error);
    return file;
  }
};

/** Build a unique filename using timestamp + optional prefix. */
const buildFileName = (originalName: string, prefix?: string): string => {
  const ts = Date.now();
  const safeName = sanitizeStorageKey(originalName) || "file";
  return prefix ? `${ts}-${sanitizeStorageKey(prefix)}-${safeName}` : `${ts}-${safeName}`;
};

/* -------------------------------------------------------------------------- */
/*                              Main Exports                                  */
/* -------------------------------------------------------------------------- */

export interface UploadOptions {
  /** Optional prefix added to filename (e.g. media-owner name, location). */
  prefix?: string;
  /** Progress callback 0-100. */
  onProgress?: (progress: number) => void;
}

/**
 * Upload a file to Supabase Storage (campaign_creatives bucket).
 * Includes compression for images, timeout protection, and sanitised keys.
 * Throws an Error with a user-friendly message on failure.
 */
export const uploadToStorage = async (
  file: File,
  fileNameHint: string,
  onProgress?: (progress: number) => void,
): Promise<string> => {
  // 1. Build safe filename
  let fileName = sanitizeStorageKey(fileNameHint) || buildFileName(file.name);
  // Ensure we add a timestamp if not already present
  if (!/^\d{10,}/.test(fileName)) {
    fileName = `${Date.now()}-${fileName}`;
  }

  // 2. Optionally compress images
  let fileToUpload = file;
  const isImage = SUPPORTED_IMAGE_FORMATS.includes(file.type);

  if (isImage) {
    onProgress?.(5);
    fileToUpload = await withTimeout(compressImage(file), COMPRESSION_TIMEOUT_MS, "Image compression");
    onProgress?.(25);
  } else {
    onProgress?.(10);
  }

  // 3. Upload to Supabase Storage
  onProgress?.(isImage ? 30 : 15);
  console.log("[fileUpload] Uploading to campaign_creatives:", {
    key: fileName,
    type: fileToUpload.type,
    size: fileToUpload.size,
  });

  const { error } = await withTimeout(
    supabase.storage.from("campaign_creatives").upload(fileName, fileToUpload, {
      cacheControl: "3600",
      upsert: true,
    }),
    UPLOAD_TIMEOUT_MS,
    "Upload",
  );

  if (error) {
    console.error("[fileUpload] Supabase upload error:", error);
    throw new Error(error.message || "Upload failed – please try again.");
  }

  onProgress?.(90);

  // 4. Get public URL
  const { data } = supabase.storage.from("campaign_creatives").getPublicUrl(fileName);
  const publicUrl = data.publicUrl;

  onProgress?.(100);

  // 5. Persist URLs in localStorage for campaign submission
  try {
    const urls: string[] = JSON.parse(localStorage.getItem("creativeUrls") || "[]");
    if (!urls.includes(publicUrl)) {
      urls.push(publicUrl);
      localStorage.setItem("creativeUrls", JSON.stringify(urls));
    }
    const allUrls: string[] = JSON.parse(localStorage.getItem("allCreativeUrls") || "[]");
    if (!allUrls.includes(publicUrl)) {
      allUrls.push(publicUrl);
      localStorage.setItem("allCreativeUrls", JSON.stringify(allUrls));
    }
  } catch {
    // ignore localStorage errors
  }

  console.log("[fileUpload] Upload success:", publicUrl);
  return publicUrl;
};

/**
 * Remove a file from Supabase Storage and clean up localStorage references.
 */
export const removeFromStorage = async (url: string): Promise<void> => {
  const fileName = url.split("/").pop();
  if (!fileName) return;

  const { error } = await supabase.storage.from("campaign_creatives").remove([fileName]);
  if (error) {
    console.warn("[fileUpload] removeFromStorage warning:", error.message);
  }

  // Clean localStorage
  try {
    const urls: string[] = JSON.parse(localStorage.getItem("creativeUrls") || "[]");
    localStorage.setItem("creativeUrls", JSON.stringify(urls.filter((u) => u !== url)));
    const allUrls: string[] = JSON.parse(localStorage.getItem("allCreativeUrls") || "[]");
    localStorage.setItem("allCreativeUrls", JSON.stringify(allUrls.filter((u) => u !== url)));
  } catch {
    // ignore
  }
};
